import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { useFormik, FormikProvider } from "formik";
import * as Yup from "yup";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import PasswordInput from "../../common_components/PasswordInput";
import "../../App.css";
import ValidationMessage from "../../common_components/Validation";
import CustomLoader from '../../common_components/CustomLoader';
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../routes/routes.constants";
import { ApiCall } from "../../helper/axios";
import AuthErrorBanner from "./AuthErrorBanner";
import AppButton from "../../common_components/AppButton";
import CommonLoader from "../../common_components/CommonLoader";
import { useDispatch } from "react-redux";
import { logout } from "../../redux/authSlice";
const validationSchema = Yup.object({
    newPassword: Yup.string()
        .trim()
        .required(ValidationMessage.passwordRequired)
        .matches(/[A-Z]/, "Password must contain at least one uppercase letter.")
        .matches(/[a-z]/, "Password must contain at least one lowercase letter.")
        .matches(/[0-9]/, "Password must contain at least one number.")
        .matches(/[@$!%*?&#]/, "Password must contain at least one special character (@, $, !, %, *, ?, &, #)."),
    confirmPassword: Yup.string()
        .trim()
        .required("Confirm password is required.")
        .oneOf([Yup.ref('newPassword'), null], "Passwords do not match."),
});
export default function ResetPassword() {
    const [loading, setLoading] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [apiError, setApiError] = useState(null);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const token = new URLSearchParams(window.location.search).get("token");
    const verifyToken = async () => {
        try {
            const res = await ApiCall("GET", `/api/auth/verify-reset-token?token=${token}`, '');
            if (res?.data?.success) {
                setTokenValid(true);
            }
            else {
                navigate(ROUTES.login.path, { replace: true });
            }
        }
        catch (err) {
            navigate(ROUTES.login.path, { replace: true });
        }
        finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        dispatch(logout());
        if (!token) {
            navigate(ROUTES.login.path, { replace: true });
            return;
        }
        verifyToken();
    }, []);
    const formik = useFormik({
        initialValues: {
            newPassword: "",
            confirmPassword: "",
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            if (formik.isValid) {
                try {
                    const payload = { ...values, token: token };
                    const res = await ApiCall('POST', "/api/auth/reset-password", payload, { authentication: token });
                    if (res?.data?.success && res?.status === 200) {
                        setApiError(null);
                        const result = res.data;
                        navigate(ROUTES.login.path, { state: result?.message });
                    }
                    else {
                        const msg = res?.response?.data?.message || 'Something went wrong';
                        setApiError({ title: msg, description: "Please try again." });
                        formik.setSubmitting(false);
                    }
                }
                catch (error) {
                    formik.setSubmitting(false);
                    setApiError({ title: 'Failed to reset password. Please try again.', description: "" });
                    console.error('error', error);
                }
            }
        },
    });
    const handleChange = (name, e) => {
        const value = e?.target?.value || '';
        formik.setFieldValue(name, value);
        if (apiError)
            setApiError(null);
    };
    const handleBlur = (name) => {
        formik.setFieldTouched(name, true);
    };
    if (loading) {
        return (_jsx(_Fragment, { children: _jsx(CommonLoader, {}) }));
    }
    if (!tokenValid)
        return _jsx(_Fragment, {});
    return (_jsxs(Box, { className: "login-page-container", children: [_jsx(Box, { className: "login-logo-container", sx: { display: 'flex', alignItems: 'center', gap: '10px' }, children: _jsx("img", { alt: "GROUP 8A Logo", src: "/assets/loginImage/logo-final.svg", className: "login-logo-img" }) }), _jsx(Paper, { elevation: 0, className: "login-form-card", children: _jsx(FormikProvider, { value: formik, children: _jsxs(Box, { component: "form", onSubmit: formik.handleSubmit, className: "login-form", children: [_jsxs(Box, { className: "login-header", children: [_jsx(Typography, { variant: "h4", className: "login-title", children: "Reset Your Password" }), _jsx(Typography, { variant: "body1", className: "login-subtitle", children: "Create a new password for your account." })] }), _jsxs(Box, { className: "login-form-fields", children: [_jsx(Box, { className: "login-form-inputs-wrapper", children: _jsxs(Box, { className: "login-form-inputs", children: [apiError && (_jsx(Box, { sx: { paddingBottom: '15px', width: '100%' }, children: _jsx(AuthErrorBanner, { message: apiError }) })), _jsx(Box, { sx: { paddingBottom: '15px', width: '100%' }, children: _jsx(PasswordInput, { label: "New Password", Placeholder: 'Enter New Password', value: formik.values.newPassword, errors: formik.errors.newPassword, touched: formik.touched.newPassword, name: "newPassword", onChange: (e) => handleChange('newPassword', e), onBlur: (e) => handleBlur('newPassword'), type: 'password' }) }), _jsx(Box, { sx: { paddingBottom: '15px', width: '100%' }, children: _jsx(PasswordInput, { label: "Confirm Password", Placeholder: 'Enter confirm password', value: formik.values.confirmPassword, errors: formik.errors.confirmPassword, touched: formik.touched.confirmPassword, name: "confirmPassword", onChange: (e) => handleChange('confirmPassword', e), onBlur: (e) => handleBlur('confirmPassword'), type: 'password' }) })] }) }), _jsx(Box, { className: "login-signin-button-container", children: _jsxs(AppButton, { type: "submit", fullWidth: true, className: "login-signin-button", variant: "contained", color: "primary", disabled: formik.isSubmitting, children: ["   ", formik.isSubmitting ? _jsx(CustomLoader, { size: 20, sx: { color: "#fff" } }) : 'Reset Password'] }) })] })] }) }) }), _jsx("div", { className: "bird-container bird-container--one", children: _jsx("div", { className: "bird bird--one", children: "\u00A0" }) }), _jsx("div", { className: "bird-container bird-container--two", children: _jsx("div", { className: "bird bird--two", children: "\u00A0" }) }), _jsx("div", { className: "bird-container bird-container--three", children: _jsx("div", { className: "bird bird--two", children: "\u00A0" }) }), _jsx("div", { className: "bird-container bird-container--four", children: _jsx("div", { className: "bird bird--two", children: "\u00A0" }) })] }));
}
